/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Injectable } from '@angular/core';
import { ListRequestModel } from 'app/api/models/list-request.model';
import { ApiDependenciesService } from 'app/api/services/api-dependencies.service';
import { User } from 'app/modules/configuration/users/user-management/models/user.model';
import { SystemRole } from 'app/shared/enumeration/system-role';
import { UserType } from 'app/shared/enumeration/user-type';
import { CollectionResponse } from 'app/shared/models/collectionResponse.model';
import { DataService } from 'app/shared/services/data.service';
import { Observable, Subject } from 'rxjs';
import { UserPermissions, UserPermissionsAction } from '../models/userpermissions.model';
import { IUsersService } from './users.service.interface';

@Injectable({ providedIn: 'root' })
export class UsersService extends DataService<User> implements IUsersService {
  private subject = new Subject<UserPermissionsAction>();

  /* UsersService must be a singleton service so that user roles are not fetched every time permissions are needed */
  private userRoles = new Array<SystemRole>();

  public userPermissions = new UserPermissions();

  /** Provides a way for clients to be notified of progress */
  public getMessage(): Observable<UserPermissionsAction> {
    return this.subject.asObservable();
  }

  constructor(protected dependencies: ApiDependenciesService) {
    super(dependencies);
    this.endpoint = '/user-Management/users';
  }

  public async toggleUserActive(userId: number) {
    return this.setEndpoint('/user-Management/change-user-status/' + userId).update();
  }

  public async createNewUser(formData: any) {
    return this.setEndpoint('/user-Management/user').save(formData);
  }

  public async updateExistingUser(userId: number, formData: any) {
    formData.id = userId;
    return this.setEndpoint('/user-Management/user').update(formData);
  }

  public async getUsersList(listRequest: ListRequestModel = null): Promise<User[]> {
    let result: User[] = [];

    await this.queryCollection(listRequest).then((queryResults: CollectionResponse<User>) => {
      result = this.mapUserObjects(queryResults);
    });
    return Promise.resolve(result);
  }

  public async getUserRoles(): Promise<SystemRole[]> {
    let model: SystemRole[];

    await this.setEndpoint('/current-user-roles')
      .getCollection()
      .then((response) => {
        model = <SystemRole[]>response;
      });

    return Promise.resolve(model);
  }

  public isAdmin(roles: SystemRole[]): boolean {
    return roles.includes(SystemRole.SystemAdmin);
  }

  /* Note:   As this is a singleton service, userPermissions will only be fetched once per login.
       Future: Get permissions from Web API? WebaPI should also have a role based permissions attibute rather than role based auth attributes */
  public async ensurePermissions(): Promise<UserPermissions> {
    const userName = this.dependencies.cookieService.getLoggedInUserName();
    if (userName === '') {
      if (this.userPermissions.isInitialised) {
        this.userPermissions = new UserPermissions();
      }

      // No permissions
      this.subject.next(new UserPermissionsAction('Current user has no permissions'));
      return Promise.resolve(this.userPermissions);
    }

    if (this.userPermissions.isInitialised) {
      if (userName === this.userPermissions.userName) {
        return Promise.resolve(this.userPermissions);
      }

      // Application has not changed but different user now logged in
      this.userPermissions = new UserPermissions();
    }

    this.userRoles = await this.getUserRoles();

    let userType = this.dependencies.cookieService.getLoggedInUserType();

    this.userPermissions.action.menu.available = true;
    this.userPermissions.action.configurationMenu.manageUsers = this.hasRolePermission([SystemRole.SystemAdmin]);
    this.userPermissions.action.configurationMenu.accessDatabaseAudit = this.hasRolePermission([SystemRole.SystemAdmin]);

    this.userPermissions.action.headerMenu.showKioskLink =
      (userType === UserType.Organiser || userType === UserType.OrganiserAndCustomer) && !this.hasRolePermission([SystemRole.SystemAdmin]);
    this.userPermissions.isSuperUser = this.hasRolePermission([SystemRole.SystemAdmin]);
    this.userPermissions.isOrganiserUser =
      (userType === UserType.Organiser || userType === UserType.OrganiserAndCustomer) && !this.hasRolePermission([SystemRole.SystemAdmin]);
    this.userPermissions.isCustomerServicesUser = this.hasRolePermission([SystemRole.CustomerServices])
    this.userPermissions.isAccountManager = this.hasRolePermission([SystemRole.AccountManager]);
    this.userPermissions.isInternalOrbitUser = userType === UserType.InternalOrbit;
    this.userPermissions.action.headerMenu.showResetPassword = this.userPermissions.isOrganiserUser;

    this.userPermissions.markAsInitialised(userName);

    this.subject.next(new UserPermissionsAction('Permissions are available'));
    return Promise.resolve(this.userPermissions);
  }

  public async hasPermission(permission: string): Promise<boolean> {
    if (!permission) {
      return Promise.resolve(false);
    }

    await this.ensurePermissions();
    const val = this.recompose(this.userPermissions, permission);
    return Promise.resolve(val);
  }

  private hasRolePermission(roles: Array<SystemRole>): boolean {
    roles.push(SystemRole.SystemAdmin);
    return this.userHasAnyRole(roles);
  }

  private userHasAnyRole(roles: SystemRole[]): boolean {
    for (const role of roles) {
      const e = this.userRoles.find((r) => r === role);

      if (e != null) {
        return true;
      }
    }

    return false;
  }

  private recompose(obj: any, s: string) {
    const parts = s.split('.');
    const newObj = obj[parts[0]];

    if (parts[1]) {
      parts.splice(0, 1);
      const newString = parts.join('.');
      return this.recompose(newObj, newString);
    }

    return newObj;
  }

  private mapUserObjects(queryResults: CollectionResponse<User>): User[] {
    const result: User[] = [];

    queryResults.items.forEach((user) => {
      result.push(new User(user.id, user.username, user.firstName, user.surname, user.email, user.mobileNumber, user.isActive, user.roles));
    });

    return result;
  }
}
